import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { getDatabaseRepresentation } from '../../utils';

interface DisplayJSONProps {
  open: boolean;
  onClose: () => void;
  data: any;
  title?: string;
  subtitle?: string;
}

const DisplayJSON: React.FC<DisplayJSONProps> = ({
  open,
  onClose,
  data,
  title = 'JSON Data',
  subtitle,
}) => {
  const handleCopyJSON = () => {
    if (!data) return;
    const jsonString = JSON.stringify(getDatabaseRepresentation(data), null, 2);
    navigator.clipboard.writeText(jsonString);
  };

  // Don't render if not open or no data
  if (!open || !data) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      // Sit above the custom UserModal overlay (z-index 1400) so the JSON view
      // opened from within it renders on top.
      sx={{ zIndex: 1500 }}
      PaperProps={{
        sx: { maxHeight: '90vh' }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          {title}
          {subtitle && (
            <Typography variant="subtitle2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: 'grey.500' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ position: 'relative' }}>
          <IconButton
            onClick={handleCopyJSON}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <ContentCopyIcon />
          </IconButton>
          <Paper sx={{ 
            p: 2, 
            backgroundColor: '#f5f5f5', 
            maxHeight: '70vh', 
            overflow: 'auto'
          }}>
            <Typography
              variant="body2"
              component="pre"
              sx={{ 
                margin: 0,
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}
            >
              {JSON.stringify(getDatabaseRepresentation(data), null, 2)}
            </Typography>
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button onClick={handleCopyJSON} color="primary">
          Copy JSON
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DisplayJSON; 